import { CloseOutlined, LoadingOutlined } from "@ant-design/icons";
import { useGetIdentity, useNavigation } from "@refinedev/core";
import { Button, Col, ConfigProvider, Modal, notification, Row, Spin } from "antd";
import { getBundles } from "api/bundles";
import { createNewPayment } from "api/credits";
import { useEffect, useState } from "react";
import classes from "./Credits.module.css";
import { UserIdentityData } from "types/props";

type Bundle = {
	id?: string;
	credit: number;
	amount: number;
	description?: string;
};

export const Credits: React.FC = () => {
	const { data: user } = useGetIdentity<UserIdentityData>();

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [selectedBundle, setSelectedBundle] = useState<any>();
	const [isPaying, setIsPaying] = useState(false);
	const [bundles, setBundles] = useState<Bundle[]>([]);
	const [loading, setLoading] = useState(false);

	const { push } = useNavigation();

	const queryParameters = new URLSearchParams(window.location.search);

	const status = queryParameters.get("success");
	const credits = queryParameters.get("credits");
	const amount = queryParameters.get("amount");
	const txData = queryParameters.get("txData"); // Can be used to show txid.

	const showModal = () => {
		setIsModalOpen(true);
	};

	const handleOk = () => {
		setIsModalOpen(false);
	};

	const handleCancel = () => {
		setIsModalOpen(false);
	};

	const handlePayment = async () => {
		try {
			setIsPaying(true);
			const paymentResponse = await createNewPayment({
				creditId: selectedBundle.id,
				amount: selectedBundle.amount,
				credits: selectedBundle.credit,
				bundleId: selectedBundle.id,
			});
			// console.log("paymentResponse:", paymentResponse);
			return;
		} catch (err) {
			// console.log("Err in payment", err);
		} finally {
			setIsPaying(false);
		}
	};

	useEffect(() => {
		(async () => {
			if (user?.approved) {
				setLoading(true);
				const { data } = await getBundles();

				setBundles(
					data.data.map((item: any) => ({
						...item,
						amount:
							item.amount - (item.amount * user.discount) / 100,
					}))
				);
				setLoading(false);
			}
		})();
	}, [user]);

	return (
		<Row className={classes.jobsContainer}>
			<Col span={8}>
				<Row className={classes.container}>
					<Row className={classes.mentions}>
						<Row>QuiX Credits</Row>
						<Row className={classes.totalBalance}>
							<Col span={24}>Free Credits</Col>
						</Row>
						<Row>{user?.freeCredits}</Row>
						{user?.approved ? (
							<>
								<Row className={classes.totalBalance}>
									<Col span={24}>Total Credits</Col>
								</Row>
								<Row>{user?.credits}</Row>
							</>
						) : (
							<Row className={classes.totalBalance}>
								You must be approved by the QuiX team before
								purchasing Credits.
							</Row>
						)}
					</Row>
					<Row className={classes.buttonContainer}>
						<Button
							className={classes.CreditButton}
							onClick={async () => {
								if (user?.approved) {
									showModal();
								} else {
									window.location.href =
										"mailto:sales@quixquantum.com";
									notification.info({
										message: "",
										description: (
											<div>
												<p>
													Please send an email to
													sales@quixquantum.com with
													your:
												</p>
												<ul>
													<li>Name</li>
													<li>
														Email address associated
														with your account
													</li>
													<li>Organization</li>
													<li>
														Purpose of you usage
													</li>
												</ul>
												<p>
													and we will get back to you
													shortly!
												</p>
											</div>
										),
										duration: 20,
									});
								}
							}}
						>
							{user?.approved ? "Buy Credits" : "Request Credits"}
						</Button>
					</Row>
				</Row>
				<div className={classes.footer}>
					{" "}
					Contact our
					<span className={classes.salesDepartment}>
						&nbsp;Sales Department
					</span>
					&nbsp; for information on QuiX Credits
				</div>
			</Col>
			<ConfigProvider
				theme={{
					token: {
						colorBgBase: "#1A1D38",
					},
					components: {
						Modal: {
							contentBg: "#1A1D38",
						},
					},
				}}
			>
				<Modal
					footer={null}
					onOk={handleOk}
					open={isModalOpen}
					centered
					className={classes.PayPopup}
					closable={false}
				>
					<Row
						justify="space-between"
						className={classes.ModalTitleCont}
					>
						<h3 className={classes.ModalTitle}>Add credits</h3>
						<CloseOutlined
							style={{ color: "#fff" }}
							onClick={handleCancel}
							className={classes.ModalCloseIcon}
						/>
					</Row>
					<Row>
						<Row className={classes.bundleSelector}>
							<h4>Select Bundle</h4>
						</Row>

						{bundles.map((item, index) => (
							<div
								onClick={() => setSelectedBundle(item)}
								key={item.id}
								style={{
									display: "flex",
									flexDirection: "row",
									width: "100%",
									border:
										selectedBundle?.id === item.id
											? "1px solid white"
											: "1px solid grey",
									padding: "16px",
									margin: "16px 8px",
									borderRadius: "2px",
									cursor: "pointer",
									alignItems: "center",
								}}
							>
								<div
									style={{
										border: "1px solid #FF5A17",
										width: "16px",
										height: "16px",
										borderRadius: "100%",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
									}}
								>
									{selectedBundle?.id === item.id && (
										<div
											style={{
												background: "#FF5A17",
												width: "8px",
												height: "8px",
												borderRadius: "100%",
											}}
										/>
									)}
								</div>
								<div
									style={{ color: "#fff", padding: "0 8px" }}
								>
									Bundle {index + 1}
								</div>

								<div style={{ flex: 1 }} />

								<div
									style={{
										color: "#fff",
										opacity:
											selectedBundle?.id === item.id
												? 1
												: "0.5",
									}}
								>
									{item.credit} Credits for {item.amount}{" "}
									Euros
								</div>
							</div>
						))}
					</Row>
					<Button
						onClick={() => handlePayment()}
						className={classes.PayButton}
					>
						{isPaying ? <LoadingOutlined /> : "Pay"}
					</Button>

					<Row className={classes.description}>
						Your personal data will be used to process your order,
						support your experience throughout this website, and for
						other purposes described in our privacy policy.
					</Row>
				</Modal>

				<Modal
					footer={null}
					onOk={handleOk}
					open={status === "true"}
					centered
					className={classes.PayPopup}
					closable={true}
					style={{ padding: "2rem" }}
					onCancel={() => {
						push("/settings/credits");
					}}
				>
					{" "}
					{/* <Row justify="end">
					<CloseOutlined
						style={{ color: "#fff" }}
						// onClick={() => push("/settings/credits")}
						className={classes.ModalCloseIcon}
					/>
				</Row> */}
					<Row>
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								justifyContent: "center",
								width: "100%",
								marginTop: "24px",
							}}
						>
							<img src={"/check_icon.svg"} alt="check-icon" />
							<h1 style={{ color: "#fff" }}>
								Your Payment was successful!
							</h1>
							<div style={{ color: "#fff", opacity: 0.7 }}>
								Your purchased credits will reflect in your
								balance in a few minutes.
							</div>
						</div>

						<Spin spinning={loading}>
							<div
								style={{
									display: "flex",
									flexDirection: "column",
									// width: "100%",
									border: "1px solid grey",
									padding: "16px",
									margin: "16px 16px",
									borderRadius: "2px",
									cursor: "pointer",
									alignItems: "center",
								}}
							>
								<h4>Order Review</h4>
								<div
									style={{
										display: "flex",
										flexDirection: "row",
										width: "100%",
										alignItems: "center",
									}}
								>
									<div
										style={{
											color: "#fff",
											padding: "0 8px",
										}}
									>
										{bundles.length > 0
											? `Bundle ${
													bundles.findIndex(
														(bundle) =>
															bundle.amount ===
																Number(
																	amount
																) &&
															bundle.credit ===
																Number(credits)
													) + 1
												}`
											: "------"}
									</div>

									<div style={{ flex: 1 }} />

									<div
										style={{
											color: "#fff",
											opacity: "0.5",
										}}
									>
										{bundles.length > 0
											? `${credits} Credits for ${amount} Euros`
											: "------ Credits for ------ Euros"}
									</div>
								</div>
							</div>
						</Spin>
					</Row>
					<div
						style={{
							width: "40%",
							margin: "auto",
						}}
					>
						<Button
							style={{
								borderRadius: "24px",
								marginBottom: "8px",
							}}
							onClick={() => {
								push("/settings/credits");
							}}
							className={classes.PayButton}
						>
							Continue
						</Button>
					</div>
				</Modal>

				<Modal
					footer={null}
					onOk={handleOk}
					open={status === "false"}
					centered
					className={classes.PayPopup}
					closable={true}
					onCancel={() => {
						push("/settings/credits");
					}}
				>
					{/* <Row justify="end">
					<CloseOutlined
						style={{ color: "#fff" }}
						onClick={() => push("/settings/credits")}
						className={classes.ModalCloseIcon}
					/>
				</Row> */}
					<Row>
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								justifyContent: "center",
								width: "100%",
								marginTop: "24px",
							}}
						>
							<img src={"/export_icon.svg"} />
							<h1 style={{ color: "#fff" }}>
								Your Payment was unsuccessful!
							</h1>
							<div style={{ color: "#fff", opacity: 0.7 }}>
								There was an issue in processing your payment.
								Please try again.
							</div>
						</div>

						<Spin spinning={loading}>
							<div
								style={{
									display: "flex",
									flexDirection: "column",
									// width: "100%",
									border: "1px solid grey",
									padding: "16px",
									margin: "16px 8px",
									borderRadius: "2px",
									cursor: "pointer",
									alignItems: "center",
								}}
							>
								<h4>Order Review</h4>
								<div
									style={{
										display: "flex",
										flexDirection: "row",
										width: "100%",
										alignItems: "center",
									}}
								>
									<div
										style={{
											color: "#fff",
											padding: "0 8px",
										}}
									>
										{bundles.length > 0
											? `Bundle ${
													bundles.findIndex(
														(bundle) =>
															bundle.amount ===
																Number(
																	amount
																) &&
															bundle.credit ===
																Number(credits)
													) + 1
												}`
											: "------"}
									</div>

									<div style={{ flex: 1 }} />

									<div
										style={{
											color: "#fff",
											opacity: "0.5",
										}}
									>
										{bundles.length > 0
											? `${credits} Credits for ${amount} Euros`
											: "------ Credits for ------ Euros"}
									</div>
								</div>
							</div>
						</Spin>
					</Row>
					<div
						style={{
							width: "40%",
							margin: "auto",
						}}
					>
						<Button
							style={{
								borderRadius: "24px",
								marginBottom: "8px",
							}}
							onClick={() => {
								push("/settings/credits");
							}}
							className={classes.PayButton}
						>
							Retry Payment
						</Button>
					</div>
				</Modal>
			</ConfigProvider>
		</Row>
	);
};
